import { useMemo } from 'react';

import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types/next-editor-plugin';
import { useSharedPluginStateWithSelector } from '@atlaskit/editor-common/useSharedPluginStateWithSelector';
import { expVal } from '@atlaskit/platform-feature-experiments/exp-val';
import { UNSAFE_expValNoExposure } from '@atlaskit/platform-feature-experiments/unsafe-exp-val-no-exposure';
import type { RemixContentComponentDefinition } from '@atlassian/malleable-ui-components/types';

import type { MalleableUiPlugin } from '../../malleableUiPluginType';
import { isSelectionUnremixable } from './selection-remixability';

const UNREMIXABLE_SELECTION_EXPERIMENT = 'platform_editor_disable_unremixable_selection';

export const useIsSelectionUnremixable = ({
	api,
	isSelectionRemixable,
	isSurfaceAvailable,
}: {
	api: ExtractInjectionAPI<MalleableUiPlugin>;
	isSelectionRemixable?: RemixContentComponentDefinition['isSelectionRemixable'];
	isSurfaceAvailable: boolean;
}): boolean => {
	const { selection, selectionContext, isViewMode } = useSharedPluginStateWithSelector(
		api,
		['selection', 'aiExperience', 'editorViewMode'],
		(states) => ({
			selection: states.selectionState?.selection,
			selectionContext: states.aiExperienceState?.selectionContext,
			isViewMode: states.editorViewModeState?.mode === 'view',
		}),
	);

	const normalizeSelection =
		!isViewMode &&
		UNSAFE_expValNoExposure('cc-ce-mau-remix-editor-selection-boundaries', 'isEnabled', false);
	const isUnremixable = useMemo(
		() =>
			isSurfaceAvailable &&
			isSelectionRemixable !== undefined &&
			isSelectionUnremixable(selection, selectionContext, isSelectionRemixable, normalizeSelection),
		[isSurfaceAvailable, isSelectionRemixable, selection, selectionContext, normalizeSelection],
	);
	if (!isUnremixable) {
		return false;
	}

	return expVal(UNREMIXABLE_SELECTION_EXPERIMENT, 'isEnabled', false) === true;
};
